import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Self-contained waitlist email form. Each instance owns its own email + loading
 * state so multiple forms on the page never share or clear each other's input.
 */
export const WaitlistForm = ({ idPrefix, dark = false, ctaLabel = 'Join the waitlist', withArrow = false, source, onJoined }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/waitlist/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(source ? { email, source } : { email }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success("You're on the list", { description: data.message });
        setEmail('');
        onJoined?.(data);
      } else {
        toast.error('Almost there', { description: data.message || 'Please try again.' });
      }
    } catch {
      toast.error('Something went wrong', { description: 'Please try again in a moment.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex max-w-md flex-col gap-3 sm:flex-row"
      data-testid={`${idPrefix}-signup-form`}
    >
      <Input
        type="email"
        required
        placeholder="you@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={
          dark
            ? 'h-12 flex-1 rounded-full border-background/20 bg-background/10 px-5 text-base text-background placeholder:text-background/50 focus-visible:ring-primary'
            : 'h-12 flex-1 rounded-full border-border bg-card px-5 text-base shadow-sm focus-visible:ring-primary'
        }
        data-testid={`${idPrefix}-email-input`}
      />
      <Button
        type="submit"
        disabled={loading}
        className="h-12 rounded-full bg-primary px-7 font-semibold text-primary-foreground shadow-md transition-transform hover:bg-primary/90 active:scale-95"
        data-testid={`${idPrefix}-submit-button`}
      >
        {loading ? 'Joining…' : ctaLabel}
        {withArrow && <ArrowRight className="ml-1.5 h-4 w-4" />}
      </Button>
    </form>
  );
};
